import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from './ThemeContext';

const STORAGE_KEY = 'novelday_reader_settings_v3';

export const READER_FONT_OPTIONS = [
  {
    value: 'sans',
    label: 'Sans-serif',
    stack: "var(--font-family-base, 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif)",
  },
  {
    value: 'sans-rounded',
    label: 'Rounded Sans',
    stack: "'Nunito', 'Quicksand', 'Segoe UI', 'Helvetica Neue', sans-serif",
  },
  {
    value: 'serif',
    label: 'Serif',
    stack: "'Merriweather', 'Georgia', 'Palatino Linotype', 'Times New Roman', serif",
  },
  {
    value: 'serif-classic',
    label: 'Classic Serif',
    stack: "'Garamond', 'Baskerville', 'Georgia', 'Times New Roman', serif",
  },
  {
    value: 'literata',
    label: 'Literata',
    stack: "'Literata', 'Georgia', 'Cambria', serif",
  },
  {
    value: 'lora',
    label: 'Lora',
    stack: "'Lora', 'Georgia', 'Palatino Linotype', serif",
  },
  {
    value: 'slab',
    label: 'Slab Serif',
    stack: "'Roboto Slab', 'Rockwell', 'Clarendon', 'Times New Roman', serif",
  },
  {
    value: 'humanist',
    label: 'Humanist Sans',
    stack: "'Optima', 'Candara', 'Calibri', 'Segoe UI', sans-serif",
  },
  {
    value: 'lexend',
    label: 'Lexend',
    stack: "'Lexend', 'Calibri', 'Arial', sans-serif",
  },
  {
    value: 'atkinson',
    label: 'Atkinson',
    stack: "'Atkinson Hyperlegible', 'Verdana', 'Arial', sans-serif",
  },
  {
    value: 'mono',
    label: 'Monospace',
    stack: "'Fira Code', 'SFMono-Regular', 'Menlo', 'Consolas', monospace",
  },
  {
    value: 'dyslexic',
    label: 'OpenDyslexic',
    stack: "'OpenDyslexic', 'Comic Sans MS', 'Verdana', sans-serif",
  },
];

export const READER_THEME_OPTIONS = [
  { value: 'light', label: 'Light', bg: '#ffffff', text: '#1f2933', ui: '#f8fafc', family: 'light' },
  { value: 'cream', label: 'Cream', bg: '#fdfbf7', text: '#363127', ui: '#f7f5f0', family: 'light' },
  { value: 'sepia', label: 'Sepia', bg: '#f6f1d1', text: '#5b4636', ui: '#efebd4', family: 'light' },
  { value: 'gray', label: 'Paper', bg: '#f1f1f1', text: '#2e2e2e', ui: '#e5e5e5', family: 'light' },
  { value: 'rose', label: 'Rose', bg: '#fdf2f4', text: '#4a2028', ui: '#f8e0e5', family: 'light' },
  { value: 'sage', label: 'Sage', bg: '#f0f4f0', text: '#2d3a2d', ui: '#e0e8e0', family: 'light' },
  { value: 'lavender', label: 'Lavender', bg: '#f3f0fa', text: '#2e2645', ui: '#e6e0f3', family: 'light' },
  { value: 'sunset', label: 'Sunset', bg: '#fef3e2', text: '#4a3520', ui: '#f8e8cc', family: 'light' },
  { value: 'ocean', label: 'Ocean', bg: '#eef5fa', text: '#1e3a50', ui: '#dde9f2', family: 'light' },
  { value: 'dark', label: 'Dark', bg: '#1a1b26', text: '#a9b1d6', ui: '#24283b', family: 'dark' },
  { value: 'navy', label: 'Navy', bg: '#0f172a', text: '#cbd5e1', ui: '#1e293b', family: 'dark' },
  { value: 'nord', label: 'Nord', bg: '#2e3440', text: '#d8dee9', ui: '#3b4252', family: 'dark' },
  { value: 'forest', label: 'Forest', bg: '#142318', text: '#d1d5db', ui: '#1c3123', family: 'dark' },
  { value: 'mocha', label: 'Mocha', bg: '#1e1e2e', text: '#cdd6f4', ui: '#313244', family: 'dark' },
  { value: 'black', label: 'Midnight', bg: '#000000', text: '#a3a3a3', ui: '#121212', family: 'dark' },
];

// Reader themes are grouped into a light/dark family so the reader surface can
// follow the site-wide theme without clobbering a user's specific preference
// (e.g. sepia). We remember the last-used theme per family and restore it when
// the site theme toggles.
const READER_THEME_FAMILY_KEYS = { light: 'novelday_reader_theme_light', dark: 'novelday_reader_theme_dark' };
const READER_THEME_FAMILY_DEFAULTS = { light: 'light', dark: 'dark' };

export const familyOfTheme = (themeValue) => {
  const option = READER_THEME_OPTIONS.find((t) => t.value === themeValue);
  return option?.family === 'dark' ? 'dark' : 'light';
};

const readFamilyTheme = (family) => {
  if (typeof window === 'undefined') {
    return READER_THEME_FAMILY_DEFAULTS[family] ?? 'light';
  }
  try {
    const stored = window.localStorage.getItem(READER_THEME_FAMILY_KEYS[family]);
    if (!stored) return READER_THEME_FAMILY_DEFAULTS[family] ?? 'light';
    const option = READER_THEME_OPTIONS.find((t) => t.value === stored && t.family === family);
    return option ? option.value : READER_THEME_FAMILY_DEFAULTS[family] ?? 'light';
  } catch (_error) {
    return READER_THEME_FAMILY_DEFAULTS[family] ?? 'light';
  }
};

const writeFamilyTheme = (family, themeValue) => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(READER_THEME_FAMILY_KEYS[family], themeValue);
  } catch (_error) {
    // ignore storage failures (private mode, quota, etc.)
  }
};

// Curated author color palette for the chapter editor's multi-color text and
// highlight formatting. Each named color has a foreground + background variant
// for BOTH light and dark reader themes, so an author's color choice
// auto-adjusts to stay readable on any reader theme (e.g. crimson text is
// #b91c1c on light themes, #f87171 on dark themes). The editor applies these
// as Quill class-based attributors (ql-textcolor-crimson, ql-highlight-crimson)
// rather than inline styles, so the reader can re-map each class to the
// theme-appropriate variant via the --author-* CSS variables emitted by
// resolveReaderStyleVariables. The backend HtmlSanitizer keeps any ql-* class,
// so these persist without backend changes.
export const AUTHOR_COLOR_PALETTE = [
  { value: 'crimson', label: 'Crimson', light: '#b91c1c', dark: '#f87171', lightBg: '#fee2e2', darkBg: '#7f1d1d' },
  { value: 'rust', label: 'Rust', light: '#9a3412', dark: '#fb923c', lightBg: '#ffedd5', darkBg: '#7c2d12' },
  { value: 'gold', label: 'Gold', light: '#b45309', dark: '#fbbf24', lightBg: '#fef3c7', darkBg: '#78350f' },
  { value: 'amber', label: 'Amber', light: '#a16207', dark: '#fde047', lightBg: '#fef9c3', darkBg: '#713f12' },
  { value: 'sage', label: 'Sage', light: '#3f6212', dark: '#bef264', lightBg: '#ecfccb', darkBg: '#365314' },
  { value: 'forest', label: 'Forest', light: '#166534', dark: '#4ade80', lightBg: '#dcfce7', darkBg: '#052e16' },
  { value: 'emerald', label: 'Emerald', light: '#047857', dark: '#34d399', lightBg: '#d1fae5', darkBg: '#064e3b' },
  { value: 'teal', label: 'Teal', light: '#0f766e', dark: '#2dd4bf', lightBg: '#ccfbf1', darkBg: '#134e4a' },
  { value: 'sky', label: 'Sky', light: '#0369a1', dark: '#38bdf8', lightBg: '#e0f2fe', darkBg: '#0c4a6e' },
  { value: 'royal', label: 'Royal', light: '#1d4ed8', dark: '#60a5fa', lightBg: '#dbeafe', darkBg: '#1e3a8a' },
  { value: 'navy', label: 'Navy', light: '#172554', dark: '#93c5fd', lightBg: '#e0e7ff', darkBg: '#0f172a' },
  { value: 'plum', label: 'Plum', light: '#7e22ce', dark: '#c084fc', lightBg: '#f3e8ff', darkBg: '#581c87' },
  { value: 'magenta', label: 'Magenta', light: '#a21caf', dark: '#e879f9', lightBg: '#fae8ff', darkBg: '#701a75' },
  { value: 'rose', label: 'Rose', light: '#be185d', dark: '#fb7185', lightBg: '#fce7f3', darkBg: '#831843' },
  { value: 'slate', label: 'Slate', light: '#475569', dark: '#94a3b8', lightBg: '#f1f5f9', darkBg: '#334155' },
  { value: 'ink', label: 'Ink', light: '#1f2937', dark: '#e5e7eb', lightBg: '#e5e7eb', darkBg: '#1f2937' },
];

export const READER_ALIGN_OPTIONS = [
  { value: 'left', label: 'Left align' },
  { value: 'justify', label: 'Justify' },
];

export const READER_CONTENT_WIDTH_OPTIONS = [
  { value: 'wide', label: 'Full width' },
  { value: 'balanced', label: 'Comfortable' },
  { value: 'narrow', label: 'Focused' },
];

export const READER_SETTINGS_RANGES = {
  fontSize: { min: 12, max: 36, step: 1 },
  lineHeight: { min: 1.3, max: 2.6, step: 0.05 },
  letterSpacing: { min: -0.05, max: 0.12, step: 0.01 },
  paragraphSpacing: { min: 0, max: 2.5, step: 0.1 },
  paragraphIndent: { min: 0, max: 4, step: 0.5 },
};

export const defaultReaderSettings = {
  fontFamily: 'sans',
  fontSize: 18,
  lineHeight: 1.9,
  letterSpacing: 0,
  paragraphSpacing: 0.8,
  paragraphIndent: 0,
  contentWidth: 'wide',
  theme: 'light',
  textAlign: 'left',
  // When true, the reader honors the author's typography (ql-font-* classes
  // AND inline first-line text-indent) instead of forcing the reader's own
  // typeface/indent. Defaults to true: author typography is part of the
  // work (like print book design) and most readers never open settings, so
  // opt-out maximizes author intent. Readers who prefer their own typeface
  // can disable it — the stored value persists, and anyone who customized
  // settings before this change already has an explicit false stored
  // (e.g. OpenDyslexic users stay protected).
  useAuthorTypography: true,
};

const FONT_STACK_MAP = READER_FONT_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.stack;
  return acc;
}, {});

// The list of font-family values (keys of READER_FONT_OPTIONS), exported so
// the chapter editor can build its Quill FontClass whitelist from the same
// single source of truth the reader uses.
export const READER_FONT_VALUES = READER_FONT_OPTIONS.map((option) => option.value);

// The list of author color values (keys of AUTHOR_COLOR_PALETTE), exported so
// the chapter editor can build its Quill color attributor whitelists from the
// same single source of truth the reader uses.
export const AUTHOR_COLOR_VALUES = AUTHOR_COLOR_PALETTE.map((color) => color.value);

const CONTENT_WIDTH_MAP = {
  narrow: 'min(700px, 100%)',
  balanced: 'min(860px, 100%)',
  wide: 'min(100%, var(--novel-detail-max-width, 1160px))',
};

const clamp = (value, min, max) => {
  if (Number.isNaN(value)) {
    return min;
  }
  return Math.min(Math.max(value, min), max);
};

export const sanitizeReaderSettings = (rawSettings = {}) => {
  const next = { ...defaultReaderSettings, ...rawSettings };

  const fontFamily = READER_FONT_OPTIONS.some((option) => option.value === next.fontFamily)
    ? next.fontFamily
    : defaultReaderSettings.fontFamily;

  const contentWidth = READER_CONTENT_WIDTH_OPTIONS.some((option) => option.value === next.contentWidth)
    ? next.contentWidth
    : defaultReaderSettings.contentWidth;

  const theme = READER_THEME_OPTIONS.some((option) => option.value === next.theme)
    ? next.theme
    : defaultReaderSettings.theme;

  const textAlign = READER_ALIGN_OPTIONS.some((option) => option.value === next.textAlign)
    ? next.textAlign
    : defaultReaderSettings.textAlign;

  // useAuthorTypography is a boolean toggle — coerce any stored value to a
  // strict boolean so the reader class logic (chapter-reader__content--author-font)
  // never receives a truthy string like "false".
  const useAuthorTypography = next.useAuthorTypography === true;

  const fontSize = clamp(Number(next.fontSize), READER_SETTINGS_RANGES.fontSize.min, READER_SETTINGS_RANGES.fontSize.max);
  const lineHeight = clamp(Number(next.lineHeight), READER_SETTINGS_RANGES.lineHeight.min, READER_SETTINGS_RANGES.lineHeight.max);
  const letterSpacing = clamp(Number(next.letterSpacing), READER_SETTINGS_RANGES.letterSpacing.min, READER_SETTINGS_RANGES.letterSpacing.max);
  const paragraphSpacing = clamp(Number(next.paragraphSpacing), READER_SETTINGS_RANGES.paragraphSpacing.min, READER_SETTINGS_RANGES.paragraphSpacing.max);
  const paragraphIndent = clamp(Number(next.paragraphIndent), READER_SETTINGS_RANGES.paragraphIndent.min, READER_SETTINGS_RANGES.paragraphIndent.max);

  return {
    fontFamily,
    contentWidth,
    theme,
    textAlign,
    fontSize,
    lineHeight,
    letterSpacing,
    paragraphSpacing,
    paragraphIndent,
    useAuthorTypography,
  };
};

const toRem = (px) => {
  const remValue = px / 16;
  return `${Number(remValue.toFixed(3))}rem`;
};

// Build the --author-{name} / --author-{name}-bg CSS variable map for a given
// theme family. Exported so the chapter editor can inject the same variables
// for its own theme-preview toggle (independent of the reader's active theme).
export const authorColorStyleVars = (isDark) => {
  const vars = {};
  for (const color of AUTHOR_COLOR_PALETTE) {
    vars[`--author-${color.value}`] = isDark ? color.dark : color.light;
    vars[`--author-${color.value}-bg`] = isDark ? color.darkBg : color.lightBg;
  }
  return vars;
};

// Build the --font-stack-{value} CSS variable map (one per reader font).
// Exported so the chapter editor can inject the same font stacks its
// .ql-font-{value} CSS rules reference, keeping a single source of truth.
export const fontStackStyleVars = () => {
  const vars = {};
  for (const option of READER_FONT_OPTIONS) {
    vars[`--font-stack-${option.value}`] = option.stack;
  }
  return vars;
};

export const resolveReaderStyleVariables = (settings) => {
  const sanitized = sanitizeReaderSettings(settings);
  const themeConfig = READER_THEME_OPTIONS.find((t) => t.value === sanitized.theme) || READER_THEME_OPTIONS[0];
  const isDark = familyOfTheme(sanitized.theme) === 'dark';

  // Author color variables — one per palette entry, picking the light or dark
  // variant based on the active reader theme's family. The reader CSS maps
  // ql-textcolor-{name} → var(--author-{name}) and ql-highlight-{name} →
  // var(--author-{name}-bg), so a single theme switch re-maps every author
  // color to its readable counterpart automatically.
  // Font-stack variables — one per reader font option, so the reader can honor
  // the author's ql-font-{value} classes when useAuthorTypography is enabled
  // (see .chapter-reader__content--author-font rules in ChapterReader.css).
  const authorColorVars = authorColorStyleVars(isDark);
  const fontStackVars = fontStackStyleVars();

  return {
    '--reader-font-family': FONT_STACK_MAP[sanitized.fontFamily] ?? FONT_STACK_MAP[defaultReaderSettings.fontFamily],
    '--reader-font-size': toRem(sanitized.fontSize),
    '--reader-line-height': sanitized.lineHeight,
    '--reader-letter-spacing': `${Number(sanitized.letterSpacing.toFixed(3))}em`,
    '--reader-paragraph-spacing': `${Number(sanitized.paragraphSpacing.toFixed(1))}em`,
    '--reader-paragraph-indent': `${Number(sanitized.paragraphIndent.toFixed(1))}em`,
    '--reader-content-max-width': CONTENT_WIDTH_MAP[sanitized.contentWidth] ?? CONTENT_WIDTH_MAP[defaultReaderSettings.contentWidth],
    '--reader-bg': themeConfig.bg,
    '--reader-text': themeConfig.text,
    '--reader-text-align': sanitized.textAlign,
    ...authorColorVars,
    ...fontStackVars,
  };
};

const readStoredSettings = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const storedValue = window.localStorage.getItem(STORAGE_KEY);
    if (!storedValue) {
      return null;
    }
    const parsed = JSON.parse(storedValue);
    return sanitizeReaderSettings(parsed);
  } catch (error) {
    return null;
  }
};

const ReaderSettingsContext = createContext(null);

export const ReaderSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(() => readStoredSettings() ?? defaultReaderSettings);
  // useTheme() returns a safe default ({ theme: 'light' }) when no ThemeProvider
  // is present (e.g. unit tests), so this provider remains usable in isolation.
  const { theme: siteTheme } = useTheme();
  // Track the last-seen site theme value. The sync effect only acts on a genuine
  // VALUE change, not on mount. This is StrictMode-safe: on the simulated
  // remount, siteTheme is unchanged, so the effect skips — a boolean didMountRef
  // would incorrectly fire on the remount and clobber the user's saved reader
  // theme (e.g. sepia overridden to dark when the site theme is dark).
  const lastSiteThemeRef = useRef(siteTheme);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  // Record the current reader theme as the last-used for its light/dark family,
  // so a later site-theme toggle restores the user's preferred theme within that
  // family (e.g. sepia is remembered when switching back to light mode).
  useEffect(() => {
    writeFamilyTheme(familyOfTheme(settings.theme), settings.theme);
  }, [settings.theme]);

  // When the site-wide theme changes, follow it: switch the reader theme to the
  // last-used theme of the matching family. Only fires on a genuine site-theme
  // change (not on mount or StrictMode's simulated remount). One-directional:
  // changing the reader theme never changes the site theme (picking sepia
  // shouldn't force the whole site into light mode).
  useEffect(() => {
    if (lastSiteThemeRef.current === siteTheme) {
      return;
    }
    lastSiteThemeRef.current = siteTheme;
    const target = readFamilyTheme(siteTheme);
    setSettings((previous) =>
      previous.theme === target
        ? previous
        : sanitizeReaderSettings({ ...previous, theme: target })
    );
  }, [siteTheme]);

  const updateSetting = (key, value) => {
    setSettings((previous) => sanitizeReaderSettings({ ...previous, [key]: value }));
  };

  const resetSettings = () => {
    setSettings(defaultReaderSettings);
  };

  const contextValue = useMemo(
    () => ({
      settings,
      updateSetting,
      resetSettings,
    }),
    [settings],
  );

  return <ReaderSettingsContext.Provider value={contextValue}>{children}</ReaderSettingsContext.Provider>;
};

export const useReaderSettings = () => {
  const context = useContext(ReaderSettingsContext);
  if (!context) {
    throw new Error('useReaderSettings must be used within a ReaderSettingsProvider');
  }
  return context;
};

export const readerContentWidthMap = CONTENT_WIDTH_MAP;
export const readerFontStackMap = FONT_STACK_MAP;
